import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import Button from "../../components/common/Button";
import { api } from "../../services/api";
import {
  Close,
  CloudUpload,
  Add,
} from "@mui/icons-material";
import UploadAppModal from "./UploadAppModal";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.lg};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }
`;

const Checkbox = styled.input`
  margin-right: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const DeviceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  max-height: 200px;
  overflow-y: auto;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surfaceElevated};
`;

const DeviceItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
  }
`;

const AppSelect = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  align-items: center;
`;

const LoadingText = styled.div`
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  font-size: 14px;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  text-align: center;
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 14px;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  background-color: #fee2e2;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const LambdaTestModal = ({
  isOpen,
  onClose,
  onRun,
  testType: initialTestType,
  testName,
}) => {
  const { isDark } = useTheme();
  const theme = { isDark };

  const [platform, setPlatform] = useState("android");
  const [testType, setTestType] = useState(
    initialTestType || "espresso"
  );
  const [appId, setAppId] = useState("");
  const [testSuite, setTestSuite] = useState("");
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState(
    []
  );
  const [apps, setApps] = useState([]);
  const [appVersions, setAppVersions] = useState([]);
  const [loadingDevices, setLoadingDevices] =
    useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingAppVersions, setLoadingAppVersions] =
    useState(false);
  const [appSource, setAppSource] = useState("lambdatest"); // 'lambdatest' or 'appversions'
  const [queueTimeout, setQueueTimeout] = useState(300);
  const [idleTimeout, setIdleTimeout] = useState(120);
  const [deviceLog, setDeviceLog] = useState(true);
  const [network, setNetwork] = useState(true);
  const [build, setBuild] = useState("");
  const [geoLocation, setGeoLocation] = useState("US");
  const [error, setError] = useState("");
  const [uploadModalOpen, setUploadModalOpen] =
    useState(false);

  const fetchAppVersions = useCallback(async () => {
    setLoadingAppVersions(true);
    try {
      const data = await api.getAppVersions({
        filter: { platform },
      });
      setAppVersions(data.results || []);
    } catch (err) {
      console.error("Error fetching app versions:", err);
    } finally {
      setLoadingAppVersions(false);
    }
  }, [platform]);

  const fetchDevices = useCallback(async () => {
    setLoadingDevices(true);
    setError("");
    try {
      const data = await api.getLambdaTestDevices();
      const platformDevices = (data || [])
        .filter(
          (d) =>
            d.platformName?.toLowerCase() ===
            platform.toLowerCase()
        )
        .map((device, index) => ({
          ...device,
          // Create unique deviceId if not present
          deviceId:
            device.deviceId ||
            `${device.deviceName}-${device.platformVersion}-${index}`,
        }));
      setDevices(platformDevices);
    } catch (err) {
      setError(
        "Cihazlar gətirilə bilmədi: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setLoadingDevices(false);
    }
  }, [platform]);

  const fetchApps = useCallback(async () => {
    setLoadingApps(true);
    try {
      const data = await api.getLambdaTestApps(platform);
      setApps(data.data || []);
    } catch (err) {
      console.error("Error fetching apps:", err);
    } finally {
      setLoadingApps(false);
    }
  }, [platform]);

  useEffect(() => {
    if (isOpen) {
      fetchDevices();
      fetchApps();
      fetchAppVersions();
      const buildName = testName
        ? `Fit ${testName} ${platform} Test - ${new Date().toLocaleString(
            "az-AZ"
          )}`
        : `Fit ${platform} Test - ${new Date().toLocaleString(
            "az-AZ"
          )}`;
      setBuild(buildName);
    }
  }, [
    isOpen,
    platform,
    testName,
    fetchDevices,
    fetchApps,
    fetchAppVersions,
  ]);

  const handleUploadSuccess = (uploadResult) => {
    // Refresh apps list
    fetchApps();
    // Set the uploaded app ID
    if (uploadResult.app_id) {
      setAppId(uploadResult.app_id);
    }
  };

  const handleDeviceToggle = (deviceId) => {
    if (!deviceId) {
      return;
    }

    setSelectedDevices((prev) => {
      if (prev.includes(deviceId)) {
        return prev.filter((id) => id !== deviceId);
      } else {
        return [...prev, deviceId];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!appId) {
      setError("App ID tələb olunur");
      return;
    }

    if (selectedDevices.length === 0) {
      setError("Ən azı bir cihaz seçilməlidir");
      return;
    }

    if (!testSuite) {
      setError("Test Suite ID tələb olunur");
      return;
    }

    const selectedDeviceNames = devices
      .filter((d) => selectedDevices.includes(d.deviceId))
      .map((d) => d.deviceName);

    const config = {
      appId: appId.startsWith("lt://")
        ? appId
        : `lt://${appId}`,
      testSuite: testSuite.startsWith("lt://")
        ? testSuite
        : `lt://${testSuite}`,
      device: selectedDeviceNames,
      queueTimeout: parseInt(queueTimeout),
      idleTimeout: parseInt(idleTimeout),
      deviceLog,
      network,
      build,
      testType,
    };

    if (testType === "espresso") {
      config.geoLocation = geoLocation;
    }

    // Determine test type based on modal context
    // If initialTestType is 'e2e', keep it as 'e2e', otherwise use 'lambdatest'
    const finalTestType =
      initialTestType === "e2e" ? "e2e" : "lambdatest";

    onRun({
      type: finalTestType,
      platform,
      provider: "lambdatest",
      lambdatestConfig: config,
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        theme={theme}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>
            LambdaTest Konfiqurasiyası
          </ModalTitle>
          <CloseButton theme={theme} onClick={onClose}>
            <Close />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <ErrorText theme={theme}>{error}</ErrorText>
          )}

          <FormGroup theme={theme}>
            <Label theme={theme}>Platform</Label>
            <Select
              theme={theme}
              value={platform}
              onChange={(e) => {
                setPlatform(e.target.value);
                setSelectedDevices([]);
              }}
            >
              <option value="android">Android</option>
              <option value="ios">iOS</option>
            </Select>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Test Növü</Label>
            <Select
              theme={theme}
              value={testType}
              onChange={(e) => setTestType(e.target.value)}
            >
              <option value="espresso">
                Espresso (Android)
              </option>
              <option value="xcui">XCUI (iOS)</option>
            </Select>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>
              App Mənbəsi
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "normal",
                  color:
                    getTheme(isDark).colors.textSecondary,
                  marginTop: "4px",
                }}
              >
                App-i LambdaTest-dən və ya App Versions-dan
                seçin
              </div>
            </Label>
            <Select
              theme={theme}
              value={appSource}
              onChange={(e) => {
                setAppSource(e.target.value);
                setAppId(""); // Reset app ID when switching source
              }}
              style={{
                marginBottom: getTheme(isDark).spacing.sm,
              }}
            >
              <option value="lambdatest">
                LambdaTest Apps
              </option>
              <option value="appversions">
                App Versions
              </option>
            </Select>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>
              App ID (lt://APP_ID formatında)
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "normal",
                  color:
                    getTheme(isDark).colors.textSecondary,
                  marginTop: "4px",
                }}
              >
                {appSource === "lambdatest"
                  ? "App ID LambdaTest-ə yüklənmiş mobil tətbiqin (APK/IPA) unikal identifikatorudur."
                  : "App Versions-dan seçilmiş app-in LambdaTest App ID-si. Əgər yoxdursa, əvvəlcə LambdaTest-ə yükləyin."}
              </div>
            </Label>
            <AppSelect theme={theme}>
              {appSource === "lambdatest" ? (
                <>
                  <Select
                    theme={theme}
                    value={appId}
                    onChange={(e) =>
                      setAppId(e.target.value)
                    }
                    style={{ flex: 1 }}
                  >
                    <option value="">App seçin...</option>
                    {loadingApps ? (
                      <option disabled>Yüklənir...</option>
                    ) : apps.length === 0 ? (
                      <option disabled>
                        App tapılmadı. Yeni app yükləyin.
                      </option>
                    ) : (
                      apps.map((app, index) => (
                        <option
                          key={app.app_id || `app-${index}`}
                          value={app.app_id}
                        >
                          {app.name} ({app.type}) -{" "}
                          {app.app_id}
                        </option>
                      ))
                    )}
                  </Select>
                  <Input
                    theme={theme}
                    type="text"
                    placeholder="lt://APP_ID və ya manual daxil edin"
                    value={appId}
                    onChange={(e) =>
                      setAppId(e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    theme={theme}
                    onClick={() => setUploadModalOpen(true)}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <Add /> Yeni App Yüklə
                  </Button>
                </>
              ) : (
                <>
                  <Select
                    theme={theme}
                    value={appId}
                    onChange={(e) =>
                      setAppId(e.target.value)
                    }
                    style={{ flex: 1 }}
                  >
                    <option value="">
                      App Version seçin...
                    </option>
                    {loadingAppVersions ? (
                      <option disabled>Yüklənir...</option>
                    ) : appVersions.length === 0 ? (
                      <option disabled>
                        App Version tapılmadı. Əvvəlcə App
                        Versions-da app yükləyin.
                      </option>
                    ) : (
                      appVersions.map((version) => (
                        <option
                          key={version._id || version.id}
                          value={
                            version.lambdatestAppId || ""
                          }
                          disabled={
                            !version.lambdatestAppId
                          }
                        >
                          {version.platform} v
                          {version.latestVersion}
                          {version.lambdatestAppId
                            ? ` - ${version.lambdatestAppId}`
                            : " (LambdaTest-ə yüklənməyib)"}
                        </option>
                      ))
                    )}
                  </Select>
                  <Input
                    theme={theme}
                    type="text"
                    placeholder="lt://APP_ID və ya manual daxil edin"
                    value={appId}
                    onChange={(e) =>
                      setAppId(e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                </>
              )}
            </AppSelect>
            {appSource === "appversions" &&
              appVersions.length > 0 && (
                <div
                  style={{
                    marginTop: getTheme(isDark).spacing.xs,
                    fontSize: "12px",
                    color:
                      getTheme(isDark).colors.textSecondary,
                    fontStyle: "italic",
                  }}
                >
                  💡 İpucu: App Versions-da app faylını
                  yükləyib LambdaTest-ə yükləyin ki, App ID
                  avtomatik əlavə olunsun.
                </div>
              )}
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>
              Test Suite ID (lt://TestSuite_ID formatında)
            </Label>
            <Input
              theme={theme}
              type="text"
              value={testSuite}
              onChange={(e) => setTestSuite(e.target.value)}
              placeholder="lt://TestSuite_ID"
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Cihazlar</Label>
            {loadingDevices ? (
              <LoadingText theme={theme}>
                Cihazlar yüklənir...
              </LoadingText>
            ) : devices.length === 0 ? (
              <LoadingText theme={theme}>
                Cihaz tapılmadı
              </LoadingText>
            ) : (
              <DeviceList theme={theme}>
                {devices.map((device, index) => {
                  const deviceId = device.deviceId;
                  const checkboxId = `device-checkbox-${deviceId}-${index}`;
                  const isSelected =
                    selectedDevices.includes(deviceId);

                  return (
                    <DeviceItem
                      key={`device-${deviceId}`}
                      theme={theme}
                    >
                      <Checkbox
                        id={checkboxId}
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleDeviceToggle(deviceId);
                        }}
                      />
                      <label
                        htmlFor={checkboxId}
                        style={{
                          cursor: "pointer",
                          flex: 1,
                          marginLeft:
                            getTheme(isDark).spacing.xs,
                          userSelect: "none",
                        }}
                      >
                        <div>{device.deviceName}</div>
                        <div
                          style={{
                            fontSize: "12px",
                            color:
                              getTheme(isDark).colors
                                .textSecondary,
                          }}
                        >
                          {device.platformName}{" "}
                          {device.platformVersion} -{" "}
                          {device.deviceStatus}
                        </div>
                      </label>
                    </DeviceItem>
                  );
                })}
              </DeviceList>
            )}
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>
              Queue Timeout (saniyə)
            </Label>
            <Input
              theme={theme}
              type="number"
              value={queueTimeout}
              onChange={(e) =>
                setQueueTimeout(e.target.value)
              }
              min="60"
              max="10800"
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>
              Idle Timeout (saniyə)
            </Label>
            <Input
              theme={theme}
              type="number"
              value={idleTimeout}
              onChange={(e) =>
                setIdleTimeout(e.target.value)
              }
              min="30"
              max="600"
            />
          </FormGroup>

          {testType === "espresso" && (
            <FormGroup theme={theme}>
              <Label theme={theme}>
                Geo Location (Country Code)
              </Label>
              <Input
                theme={theme}
                type="text"
                value={geoLocation}
                onChange={(e) =>
                  setGeoLocation(e.target.value)
                }
                placeholder="US"
              />
            </FormGroup>
          )}

          <FormGroup theme={theme}>
            <Label theme={theme}>Build Name</Label>
            <Input
              theme={theme}
              type="text"
              value={build}
              onChange={(e) => setBuild(e.target.value)}
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>
              <Checkbox
                type="checkbox"
                checked={deviceLog}
                onChange={(e) =>
                  setDeviceLog(e.target.checked)
                }
              />
              Device Log
            </Label>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>
              <Checkbox
                type="checkbox"
                checked={network}
                onChange={(e) =>
                  setNetwork(e.target.checked)
                }
              />
              Network Log
            </Label>
          </FormGroup>

          <div
            style={{
              display: "flex",
              gap: getTheme(isDark).spacing.sm,
              marginTop: getTheme(isDark).spacing.lg,
            }}
          >
            <Button
              type="submit"
              variant="primary"
              theme={theme}
              style={{ flex: 1 }}
            >
              <CloudUpload /> Testi İşə Sal
            </Button>
            <Button
              type="button"
              variant="outline"
              theme={theme}
              onClick={onClose}
            >
              Ləğv et
            </Button>
          </div>
        </form>

        <UploadAppModal
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onUploadSuccess={handleUploadSuccess}
        />
      </ModalContent>
    </ModalOverlay>
  );
};

export default LambdaTestModal;
